import { useEffect, useRef, useState } from "react";
import { ButtonAsButton } from "@made-people/blueprint-button";
import { ResponsiveImage } from "@made-people/blueprint-responsive-image";
import { useTranslate } from "@made-people/grease-translations";
import type { Dispatch, SetStateAction } from "react";
import { useFrontendContext } from "../../../../hooks/use-frontend-context";
import { ChevronRight } from "../../../../assets/icons/ChevronRight";
import type {
  ReturnFlowState,
  ReturnItemWithLocalSize,
  ReturnReasonsTable,
  ReturnSelections,
  ReturnsLookupResponse,
} from "./non-inretrn-return-flow";

type ReturnItemsSelectionProps = {
  data: ReturnsLookupResponse;
  email: string;
  returnReasons?: ReturnReasonsTable;
  initialSelections?: ReturnSelections;
  setState: Dispatch<SetStateAction<ReturnFlowState>>;
};

export type ReturnReason = {
  code: string;
  label: string;
  categories: string[];
};

const GENERAL_CATEGORY = "general";

export const parseReturnReasons = (
  table?: ReturnReasonsTable,
): ReturnReason[] => {
  if (!table?.thead || !table?.tbody) {
    return [];
  }

  const headers = table.thead.map((cell) => cell.value);
  const categoryIndex = headers.indexOf("productCategory");
  const codeIndex = headers.indexOf("returnReasonCode");
  const labelIndex = headers.indexOf("ReturnReason");

  if (codeIndex === -1 || labelIndex === -1) {
    return [];
  }

  return table.tbody
    .map((row) => ({
      code: row.body[codeIndex]?.value ?? "",
      label: row.body[labelIndex]?.value ?? "",
      categories:
        categoryIndex === -1
          ? []
          : (row.body[categoryIndex]?.value ?? "")
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean),
    }))
    .filter((reason) => reason.code !== "" && reason.label !== "");
};

const getReasonsForCategory = (
  reasons: ReturnReason[],
  categoryUri: string | undefined,
): ReturnReason[] =>
  reasons.filter(
    (reason) =>
      reason.categories.includes(GENERAL_CATEGORY) ||
      (categoryUri !== undefined && reason.categories.includes(categoryUri)),
  );

export const formatPrice = (
  amount: number,
  currencyCode: string,
  language: string,
): string => {
  try {
    return new Intl.NumberFormat(language, {
      style: "currency",
      currency: currencyCode,
    }).format(amount);
  } catch {
    return `${amount} ${currencyCode}`;
  }
};

const ReasonDropdown = ({
  reasons,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  reasons: ReturnReason[];
  value: string;
  onChange: (code: string) => void;
  disabled: boolean;
  placeholder: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedReason = reasons.find((reason) => reason.code === value);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={placeholder}
        onClick={() => setIsOpen((open) => !open)}
        className={`flex w-full items-center justify-between gap-2 rounded-md border border-gray-300 px-4 py-3 text-left text-sm text-gray-700 outline-none focus:border-gray-700 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 ${
          selectedReason ? "bg-sand-200" : "bg-white"
        }`}
      >
        <span
          className={
            selectedReason
              ? "truncate font-normal text-gray-800"
              : "truncate font-semibold text-gray-500"
          }
        >
          {selectedReason ? selectedReason.label : placeholder}
        </span>
        <ChevronRight
          className="h-3 w-3 shrink-0 transition-transform"
          style={{ transform: isOpen ? "rotate(-90deg)" : "rotate(90deg)" }}
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label={placeholder}
          className="absolute left-0 right-0 top-full z-20 mt-2 flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md bg-white/100"
        >
          <li>
            <button
              type="button"
              role="option"
              aria-selected={value === ""}
              onClick={() => {
                onChange("");
                setIsOpen(false);
              }}
              className={`w-full rounded-md bg-white px-4 py-3 text-left text-sm shadow-sm ${
                value === "" ? "font-medium text-gray-900" : "text-gray-700"
              }`}
            >
              {placeholder}
            </button>
          </li>
          {reasons.map((reason) => {
            const isSelected = reason.code === value;

            return (
              <li key={reason.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(reason.code);
                    setIsOpen(false);
                  }}
                  className={`w-full rounded-md px-4 py-3 text-left text-sm shadow-sm ${
                    isSelected
                      ? "bg-sand-200 font-medium text-gray-900 hover:bg-sand-200"
                      : "bg-white text-gray-700 hover:bg-sand-200"
                  }`}
                >
                  {reason.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const ReturnItemCard = ({
  item,
  currencyCode,
  language,
  isReturned,
  returnReasons,
  reason,
  onReasonChange,
}: {
  item: ReturnItemWithLocalSize;
  currencyCode: string;
  language: string;
  isReturned: boolean;
  returnReasons: ReturnReason[];
  reason: string;
  onReasonChange: (code: string) => void;
}) => {
  const translate = useTranslate();
  const reasons = getReasonsForCategory(returnReasons, item.categoryUri);

  return (
    <div
      className={`flex flex-col rounded-md p-2 ${
        isReturned ? "opacity-60" : "hover:shadow-md"
      }`}
    >
      <div className="relative w-full overflow-hidden rounded-[5px] bg-gray-100 aspect-product-image">
        {isReturned && (
          <span className="absolute left-0 top-0 z-10 bg-black px-3 py-1 text-xs text-white">
            {translate("return_flow_returned_badge")}
          </span>
        )}
        {!isReturned && reason !== "" && (
          <span className="absolute left-0 top-[10px] z-10 flex items-center bg-black/50 px-4 py-1 text-sm text-white rounded-r-[3px]">
            {translate("return_flow_selected_item_badge")}
          </span>
        )}
        {item.imageUrl && (
          <ResponsiveImage
            image={{ src: item.imageUrl, alt: item.name }}
            loading="lazy"
            displaySize="240px"
            className={`h-full w-full object-cover ${
              isReturned ? "grayscale" : ""
            }`}
          />
        )}
      </div>

      <div className="mt-3 flex items-baseline justify-between gap-2">
        <span className="text-lg font-bold font-primary text-gray-900">
          {formatPrice(item.unitPrice, currencyCode, language)}
        </span>
        <span className="text-xs font-primary text-grey-500">
          {item.sku + item.variantSku}
        </span>
      </div>

      <p
        className="mt-1 truncate text-base font-primary text-gray-800"
        title={item.name}
      >
        {item.name}
      </p>
      <p className="text-base font-primary text-gray-500">
        {item.variant} – {item.localSize}
      </p>

      <div className="mt-3">
        <ReasonDropdown
          reasons={reasons}
          value={reason}
          onChange={onReasonChange}
          disabled={isReturned}
          placeholder={translate("return_flow_Items_dropdown_title")}
        />
      </div>
    </div>
  );
};

export type ReturnCard = {
  key: string;
  item: ReturnItemWithLocalSize;
  isReturned: boolean;
};

export const getTotalRefund = (cards: ReturnCard[]): number =>
  cards.reduce((sum, card) => sum + card.item.unitPrice, 0);

const sumQuantityByOrderLineId = (
  groups: { items: { orderLineId: string; quantity: number }[] }[],
): Map<string, number> => {
  const totals = new Map<string, number>();
  for (const group of groups) {
    for (const line of group.items) {
      totals.set(
        line.orderLineId,
        (totals.get(line.orderLineId) ?? 0) + line.quantity,
      );
    }
  }
  return totals;
};

export const buildReturnCards = (data: ReturnsLookupResponse): ReturnCard[] => {
  const shippedByOrderLineId = sumQuantityByOrderLineId(data.shipments);
  const returnedByOrderLineId = sumQuantityByOrderLineId(data.existingReturns);

  const returnedCards: ReturnCard[] = [];
  const activeCards: ReturnCard[] = [];

  for (const item of data.items) {
    const shipped = shippedByOrderLineId.get(item.orderLineId) ?? 0;
    const returned = returnedByOrderLineId.get(item.orderLineId) ?? 0;
    const active = shipped;

    for (let index = 0; index < returned; index += 1) {
      returnedCards.push({
        key: `${item.orderLineId}-returned-${index}`,
        item,
        isReturned: true,
      });
    }

    for (let index = 0; index < active; index += 1) {
      activeCards.push({
        key: `${item.orderLineId}-active-${index}`,
        item,
        isReturned: false,
      });
    }
  }

  return [...returnedCards, ...activeCards];
};

export const ReturnItemsSelection = ({
  data,
  email,
  returnReasons,
  initialSelections,
  setState,
}: ReturnItemsSelectionProps) => {
  const translate = useTranslate();
  const frontendContext = useFrontendContext();
  const cards = buildReturnCards(data);
  const parsedReturnReasons = parseReturnReasons(returnReasons);
  const totalToReturn = cards.filter((card) => !card.isReturned).length;
  const [selections, setSelections] = useState<ReturnSelections>(
    initialSelections ?? {},
  );
  const selectedCount = cards.filter(
    (card) => !card.isReturned && selections[card.key],
  ).length;

  return (
    <div className="mt-component-spacing-3xl mb-component-spacing-5xl px-component-spacing-sm">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium text-gray-900">
            {translate("Account_OrderDetails_OrderNumber_Label")}:{" "}
            {data.orderNumber}
          </span>
          <span className="text-sm font-medium text-gray-900">
            {translate("Return_Items_TotalToReturn", { count: totalToReturn })}
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {cards.map((card) => (
            <ReturnItemCard
              key={card.key}
              item={card.item}
              isReturned={card.isReturned}
              currencyCode={data.currencyCode}
              language={frontendContext.language}
              returnReasons={parsedReturnReasons}
              reason={selections[card.key] ?? ""}
              onReasonChange={(code) =>
                setSelections((current) => ({ ...current, [card.key]: code }))
              }
            />
          ))}
        </div>

        <div className="mt-[100px] flex justify-center">
          <div className="w-full max-w-[500px]">
            <ButtonAsButton
              label={translate("return_flow_show_summary_button")}
              type="button"
              disabled={selectedCount === 0}
              onClick={() =>
                setState({ status: "summaryState", data, email, selections })
              }
              variants={{ width: "full", rounded: "md", color: "black" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
